import React, { useEffect } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBBtn,
  MDBModalFooter,
} from "mdb-react-ui-kit";

/**
 * PreviewModal
 * - Megjelenít egy fejlécet (cím + Close)
 * - Bal oldalon/első blokkban a preview tartalom (children)
 * - Alatta/utána egy "File info" kártya a metaadatokkal
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string
 *  - isLoading: boolean
 *  - error: string | null
 *  - children: ReactNode (a tényleges preview)
 *  - meta: {
 *      name?, size?, mimeType?, storageId?, createdAt?, updatedAt?, path?, id?, node?
 *    }
 */
export default function PreviewModal({ open, onClose, title, isLoading, error, children, meta }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const prettyBytes = (n) => {
    if (typeof n !== "number") return "—";
    if (n < 1024) return `${n} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let u = -1;
    let v = n;
    do {
      v /= 1024;
      u++;
    } while (v >= 1024 && u < units.length - 1);
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[u]}`;
  };

  const prettyDate = (t) => {
    if (!t) return "—";
    try {
      // t jöhet epoch sec / ms, ISO string, stb.
      const d =
        typeof t === "number"
          ? // ha valószínűleg epoch sec (10 jegy), szorozzuk fel
            new Date(t < 3e11 ? t * 1000 : t)
          : new Date(t);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleString();
    } catch (e) {
      return "—";
    }
  };

  const info = {
    Name: meta?.name ?? meta?.node?.properties?.fileName ?? meta?.node?.name ?? "—",
    Size: prettyBytes(
      typeof meta?.size === "number"
        ? meta.size
        : typeof meta?.node?.size === "number"
          ? meta.node.size
          : typeof meta?.node?.properties?.fileSize === "number"
            ? meta.node.properties.fileSize
            : undefined,
    ),
    "MIME type":
      meta?.mimeType ??
      meta?.node?.properties?.fileMimeType ??
      guessMimeFromName(meta?.name || meta?.node?.properties?.fileName),
    "Storage Id": meta?.storageId ?? meta?.node?.properties?.fileStorageId ?? "—",
    Path: meta?.path ?? meta?.node?.path ?? "—",
    "Created at": prettyDate(meta?.createdAt ?? meta?.node?.properties?.creationTime) ?? "—",
    "Updated at": prettyDate(meta?.updatedAt ?? meta?.node?.updatedAt) ?? "—",
    "Node ID": meta?.id ?? meta?.node?.id ?? "—",
  };

  return (
    <MDBModal staticBackdrop tabIndex="-1" closeOnEsc={false} show={open} className="central-info-modal">
      <MDBModalDialog scrollable centered>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>
              <span className="title-text">{title}</span>
            </MDBModalTitle>
            <MDBBtn className="button subtle large" type="button" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.70703 8L13.8535 2.85352C14.0488 2.6582 14.0488 2.3418 13.8535 2.14648C13.6582 1.95117 13.3418 1.95117 13.1465 2.14648L8 7.29297L2.85352 2.14648C2.6582 1.95117 2.3418 1.95117 2.14648 2.14648C1.95117 2.3418 1.95117 2.6582 2.14648 2.85352L7.29297 8L2.14648 13.1465C1.95117 13.3418 1.95117 13.6582 2.14648 13.8535C2.24414 13.9512 2.37207 14 2.5 14C2.62793 14 2.75586 13.9512 2.85352 13.8535L8 8.70703L13.1465 13.8535C13.2441 13.9512 13.3721 14 13.5 14C13.6279 14 13.7559 13.9512 13.8535 13.8535C14.0488 13.6582 14.0488 13.3418 13.8535 13.1465L8.70703 8Z"
                  fill="var(--text-accent-maxi)"
                />
              </svg>
            </MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            {" "}
            <div style={{ padding: 12 }}>{children}</div>
            <div className="prevent-select-respo" style={{ marginTop: 16 }}>
              <div className="node-data-div-respo">
                <div className="folder-info-div file-info-card" style={{ width: "100%" }}>
                  <div className="title-div file-info-title">File info</div>

                  <div className="file-info-grid">
                    {Object.entries(info).map(([label, value]) => (
                      <div key={label} className="file-info-row">
                        <div className="file-info-label">{label}</div>
                        <div className="file-info-value">{String(value)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

function guessMimeFromName(name) {
  if (!name) return "—";
  const low = name.toLowerCase();
  if (low.endsWith(".pdf")) return "application/pdf";
  if (low.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (low.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (low.endsWith(".pptx")) return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (low.endsWith(".mp4")) return "video/mp4";
  if (low.endsWith(".png")) return "image/png";
  if (low.endsWith(".jpg") || low.endsWith(".jpeg")) return "image/jpeg";
  return "—";
}
